import { EChangeType, EVariableType, ModelInclusion } from '../data/Constants';
import {
  checkIsPasswordFeature,
  createConfigureRequest,
  getNameWithCode,
  getVariableFromAllSections,
  getVariableType,
  isSubModelAssigned,
} from './ConfigurationDataHelperFunctions';
import { t } from 'i18next';

import type {
  IFeatureOrFamily,
  IExtendedConfigureResponse,
  IValueType,
  IVariableAssignment,
  IConflictHierarchy,
  IVariableValueAssignment,
  IConfigurationValue,
  IAssignment,
  IConflictDialogConflictType,
  ISectionAssignments,
  IAssignmentItem,
  IConflictDataPresentationType,
  IConfigurationVariable,
} from '../../types';

// Constants for submodel parsing
const SUBMODEL_PREFIX = 'I_';
const SUBMODEL_INSTANCE_PATTERN = /_\d+$/;

interface IConflictResult {
  assignmentsToRemove: IConflictHierarchy;
}

/**
 * Extracts submodel identifiers from a variable ID
 * @param {string} variableId the variable identifier
 * @param {IExtendedConfigureResponse} configuration the configuration object
 * @returns {string[]} array of submodel identifiers
 */
const extractSubModels = (
  variableId: string,
  configuration: IExtendedConfigureResponse
): string[] => {
  return variableId
    .split('.')
    .filter(
      (idPart: string) =>
        idPart.startsWith(SUBMODEL_PREFIX) && SUBMODEL_INSTANCE_PATTERN.exec(idPart)
    )
    .map((submodelInstance: string) =>
      submodelInstance.replace(SUBMODEL_PREFIX, '').replace(SUBMODEL_INSTANCE_PATTERN, '')
    )
    .filter((m) => configuration.contextData?.models && m in configuration.contextData.models);
};

/**
 * Checks if there is a conflict based on the current assignments and the configure response
 * @param {IExtendedConfigureResponse} configuration the configure response
 * @returns {IConflictResult | null} the conflict object or null if there is no conflict
 */
export function getConflict(configuration: IExtendedConfigureResponse): IConflictResult | null {
  const removedAssignments = configuration.data?.removedAssignments?.variableAssignments;

  if (!removedAssignments?.length) {
    return null;
  }

  const filteredRemovedAssignments: IVariableAssignment[] = [];

  // Create a Map for O(n) lookup performance
  const assignmentMap = new Map<string, IAssignment>();
  configuration.assignments?.forEach((assignment) => {
    assignmentMap.set(assignment.variableId, assignment);
  });

  for (const removedAssignment of removedAssignments) {
    const subModels = extractSubModels(removedAssignment.variable.id, configuration);

    const assignment = assignmentMap.get(removedAssignment.variable.id);
    const removedEmptyAssignment = assignment
      ? checkConflictEmptyAssignments(configuration, assignment, removedAssignment)
      : false;

    if (
      !isSubModelAssigned(configuration, subModels) ||
      removedEmptyAssignment ||
      removedAssignment.variable.id.endsWith(ModelInclusion.Id)
    ) {
      /**
       * no conflict if removed assignment is in not assigned submodel
       * no conflict if assignment was empty and is removed
       * no conflict for modelInclusion variables
       */
      continue;
    }

    filteredRemovedAssignments.push(removedAssignment);
  }

  //conflict appears when previous assignments have to be removed
  return filteredRemovedAssignments.length === 0
    ? null
    : {
        assignmentsToRemove: createConflicts(configuration, filteredRemovedAssignments),
      };
}

/**
 * Checks if the empty value is present in the removed assignments
 * @param {IExtendedConfigureResponse} configuration the configuration state
 * @param {IAssignment} assignment the current assignment
 * @param {IVariableAssignment} removedAssignment the removed assignment
 * @returns {boolean} true if assignment is empty and should be ignored
 */
function checkConflictEmptyAssignments(
  configuration: IExtendedConfigureResponse,
  variable: IAssignment,
  removedAssignment: IVariableAssignment
): boolean {
  //get configuration variable from configuration data
  const configVariable = getVariableFromAllSections(configuration, variable.variableId);

  //Return false if the ISP property value is true and the variable type is string to show the conflict
  if (
    checkIsPasswordFeature(configVariable) &&
    getVariableType(configVariable) === EVariableType.String
  ) {
    return false;
  }

  return !variable.value && removedAssignment.value?.value === variable.value;
}

/**
 * Creates a hierarchical object based on model structure with conflicts
 * @param {IExtendedConfigureResponse} configuration the current configure state
 * @param {IVariableAssignment[]} removedAssignments the removed assignments
 * @returns {IConflictHierarchy} the conflict hierarchy object
 */
const createConflicts = (
  configuration: IExtendedConfigureResponse,
  removedAssignments: IVariableAssignment[]
): IConflictHierarchy => {
  const result: IConflictHierarchy = {
    conflicts: [],
  };

  for (const removedAssignment of removedAssignments) {
    const subModels = extractSubModels(removedAssignment.variable.id, configuration);
    let currentLevel = result;

    for (const subModel of subModels) {
      const subModelName = subModel
        .replace(SUBMODEL_PREFIX, '')
        .replace(SUBMODEL_INSTANCE_PATTERN, '');
      if (!currentLevel[subModelName]) {
        currentLevel[subModelName] = {
          conflicts: [],
        };
      }
      currentLevel = currentLevel[subModelName] as IConflictHierarchy;
    }
    currentLevel.conflicts?.push(removedAssignment);
  }
  return result;
};

/**
 * Creates a string with information about which value is assigned to which variable
 * @param {INamed} variable the variable object
 * @param {IConfigurationValue | IVariableValueAssignment | IValueType} value the value object
 * @param {boolean} showCode to get the user preference
 * @param {boolean} isAssignment indicates if the value was assigned to a variable or unassigned from a variable
 * @returns {string} the string with information about which value is assigned to which variable
 */
export const getLastSelectionText = (
  variable: IConfigurationVariable,
  value: IConfigurationValue | IVariableValueAssignment | IValueType,
  showCode = true,
  isAssignment = true
) => {
  const variableName = getNameWithCode(variable as IFeatureOrFamily, showCode);
  let valueName;

  if (typeof value === 'string' && value) {
    valueName = value;
  } else if (typeof value === 'object' && value?.name && value?.value) {
    const valueObj = {
      name: value.name,
      value: value.value,
      shortSalesText: value.shortSalesText,
    };
    valueName = getNameWithCode(valueObj as IFeatureOrFamily, showCode);
  } else {
    valueName =
      variable.variableType === EVariableType.Number ||
      variable.variableType === EVariableType.String
        ? variable.values.find((v) => v.assigned)?.value
        : "''";
  }
  return isAssignment
    ? ` ${variableName} ${t('labels.to')} ${valueName}`
    : ` ${valueName} ${t('labels.from')} ${variableName}`;
};
/**
 * Get list of conflicting values categorized into assignment and removal of the variable
 * @param {IConflictDialogConflictType} conflictVariable the variable whose values are conflicting
 * @param {boolean} showCode the user preference for showCode
 * @returns {string[]} list of conflicting values categorized into assignment and removal of the variable
 */
export const getConflictsList = (
  conflictVariable: IConflictDialogConflictType,
  showCode: boolean
): string[] => {
  const assigned = conflictVariable.values
    .filter((value: IVariableValueAssignment) => !value.exclude)
    .map((value: IVariableValueAssignment) => getNameWithCode(value as IFeatureOrFamily, showCode))
    .join(','); // produces: "value1,value2,value3"
  const removed = conflictVariable.values
    .filter((value: IVariableValueAssignment) => value.exclude)
    .map((value: IVariableValueAssignment) => getNameWithCode(value as IFeatureOrFamily, showCode))
    .join(','); // produces: "value1,value2,value3"
  return removed.length ? [assigned, removed] : [assigned];
};
/**
 * Reverts the latest changes and returns the request object
 * @param {IExtendedConfigureResponse} configuration the configuration object
 * @returns {object} the request object with the reverted assignments
 */
export function onRevertAssignment(configuration: IExtendedConfigureResponse) {
  const { configurationId, lastChange, savedConfiguration, assignmentsBefore } = configuration;

  if (!configurationId || !assignmentsBefore || !lastChange || !savedConfiguration) {
    return null;
  }

  let newAssignments = [...assignmentsBefore];

  switch (lastChange.type) {
    case EChangeType.Remove:
      //Assignment was removed -> Add assignment again
      newAssignments.push({
        type: 'Singleton',
        variableId: lastChange.variableId,
        value: lastChange.value,
        exclude: false,
        priority: lastChange.priority,
      });
      break;
    case EChangeType.Change:
      //Assignment was changed -> Assign previous value
      newAssignments = newAssignments.map((a) =>
        a.variableId === lastChange.variableId ? { ...a, value: lastChange.valueBefore } : a
      );
      break;
    case EChangeType.Add:
      //Assignment was added -> Remove assignment again
      newAssignments = newAssignments.filter(
        (a) => !(a.variableId === lastChange.variableId && a.value === lastChange.value)
      );
      break;
    default:
      throw new Error(`Unsupported change type: '${lastChange.type}'`);
  }
  return createConfigureRequest(
    savedConfiguration.modelContext.rootModel.id,
    configurationId,
    newAssignments
  );
}

/**
 * Converts conflict model data into ISectionAssignments format.
 * Maps each conflict's feature and option information into IAssignmentItem entries grouped by section.
 * @param {IConflictDataPresentationType} model - The conflict model data
 * @returns {ISectionAssignments} The transformed section assignments
 */

export const convertConflictToSections = (
  model: IConflictDataPresentationType
): ISectionAssignments => {
  const sections: ISectionAssignments = {};
  model.sections.forEach((section) => {
    const items: IAssignmentItem[] = [];
    section.conflicts.forEach((conflict) => {
      conflict.values.forEach((value) => {
        items.push({
          featureId: conflict.id,
          optionId: String(value.value ?? ''),
          name: value.shortSalesText ?? value.name,
        });
      });
    });
    if (items.length > 0) {
      sections[section.name] = items;
    }
  });
  return sections;
};
